import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { email, requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de agencias. Tercer catálogo del backoffice
 * en v2 y el PRIMERO con una relación en el listado: una agencia es la sucursal
 * de una empresa, así que su empresa madre viaja como referencia `cmp_` en vez
 * del ObjectId legacy.
 *
 * `code` es único POR EMPRESA (la sucursal "01" puede existir en dos empresas
 * distintas). `default` es único por empresa: marcar una desmarca la anterior de
 * la MISMA empresa y la fuerza activa. Reglas de dominio en el service: la
 * default no se desactiva, y no se eliminan bloqueadas ni la default.
 *
 * `direction` es la dirección física (nombre de wire heredado; se conserva para
 * no romper el contrato existente).
 */

/**
 * Referencia a la empresa madre, wire-safe: el `_id` no cruza el adapter, así que
 * se publica su `cmp_`. Nullable por robustez del listado —igual que la autoría—:
 * una fila con la empresa sin `publicId` presenta `null` en vez de tumbar la
 * página entera; `company` es required en el dominio, así que en la práctica
 * siempre se resuelve.
 */
export const V2AdminAgencyCompanySchema = z
  .object({
    id: PublicIdSchemas.company,
    name: z.string()
  })
  .nullable()

/** Autoría wire-safe: `usr_`, nunca el ObjectId. Nullable como en el resto de v2. */
export const V2AdminAgencyAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/** Fila del listado y del detalle. */
export const V2AdminAgencySchema = z.object({
  id: PublicIdSchemas.agency,
  name: z.string().min(1),
  /** Único (case-insensitive) dentro de la empresa. */
  code: z.string().min(1),
  /** Dirección física de la sucursal. */
  direction: z.string(),
  email: z.string(),
  phone: z.string(),
  company: V2AdminAgencyCompanySchema,
  /** De sistema: una agencia bloqueada no se puede borrar. Sólo lectura. */
  blocked: z.boolean(),
  active: z.boolean(),
  /** Única por empresa: marcarla desmarca a la anterior y la fuerza activa. */
  default: z.boolean(),
  createdBy: V2AdminAgencyAuthorSchema,
  updatedBy: V2AdminAgencyAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminAgenciesPageSchema = z.object({
  data: z.array(V2AdminAgencySchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y faceta `active` con los mismos filtros allowlist del listado. La
 * faceta ignora el filtro de su propia dimensión; `total` respeta la consulta
 * completa. La empresa no es una faceta: se filtra por nombre (texto), no es una
 * dimensión de baja cardinalidad con valor de conteo.
 */
export const V2AdminAgenciesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de agencias. `company` (`cmp_`) permite a los
 * formularios de cliente y negociación cascadear el select por empresa, y
 * `default` preselecciona la predeterminada de la empresa elegida.
 */
export const V2AdminAgencyLookupItemSchema = z.object({
  id: PublicIdSchemas.agency,
  name: z.string().min(1),
  default: z.boolean(),
  company: PublicIdSchemas.company
})

export const V2AdminAgencyLookupSchema = z.object({
  data: z.array(V2AdminAgencyLookupItemSchema)
})

/**
 * Alta de agencia. La empresa madre entra por `cmp_` (no por ObjectId como el
 * wire legacy); el service la resuelve y valida que exista. `code` se recorta y
 * debe ser único (case-insensitive) dentro de esa empresa.
 */
export const V2CreateAdminAgencySchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  code: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  direction: requiredString(),
  email: requiredString().pipe(email()),
  phone: requiredString(),
  company: PublicIdSchemas.company,
  default: z.boolean().optional(),
  active: z.boolean().optional()
})

/** Edición parcial. Un id desconocido responde 404: nunca hace upsert. */
export const V2UpdateAdminAgencySchema = V2CreateAdminAgencySchema.partial()

export type V2AdminAgencyCompany = z.infer<typeof V2AdminAgencyCompanySchema>
export type V2AdminAgencyAuthor = z.infer<typeof V2AdminAgencyAuthorSchema>
export type V2AdminAgency = z.infer<typeof V2AdminAgencySchema>
export type V2AdminAgenciesPage = z.infer<typeof V2AdminAgenciesPageSchema>
export type V2AdminAgenciesSummary = z.infer<typeof V2AdminAgenciesSummarySchema>
export type V2AdminAgencyLookupItem = z.infer<typeof V2AdminAgencyLookupItemSchema>
export type V2AdminAgencyLookup = z.infer<typeof V2AdminAgencyLookupSchema>
export type V2CreateAdminAgencyInput = z.infer<typeof V2CreateAdminAgencySchema>
export type V2UpdateAdminAgencyInput = z.infer<typeof V2UpdateAdminAgencySchema>
